import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/utils-app";
import { ChevronLeft, ChevronRight, TrendingUp, Percent, Home, Store as StoreIcon, Lock } from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export default function AdminSplitReport() {
  const { user } = useAuth();
  const isCEO = user?.role === "superadmin" && Array.isArray(user?.tags) && user.tags.includes("ceo");
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isCEO) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/split`, { params: { year, month } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao carregar relatório"); }
    finally { setLoading(false); }
  }, [year, month, isCEO]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  if (!isCEO) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 flex items-start gap-4" data-testid="split-locked">
          <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center"><Lock size={18}/></div>
          <div>
            <h1 className="font-heading font-black text-2xl">Restrito ao CEO</h1>
            <p className="text-sm text-neutral-600 mt-1">O relatório de split de pagamentos mostra números financeiros consolidados da rede e só está disponível para usuários com a tag CEO.</p>
          </div>
        </div>
      </div>
    );
  }

  const totalCommission = data?.matriz_total ?? 0;
  const totalGross = data?.grand_gross ?? 0;
  const avgTake = totalGross > 0 ? (totalCommission / totalGross) * 100 : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Financeiro</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Split de Pagamentos</h1>
          <p className="mt-2 text-sm text-neutral-600">Quanto a matriz recebeu de comissão automática por franqueado no período.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl p-1" data-testid="split-period">
          <Button size="sm" variant="ghost" onClick={() => changeMonth(-1)} data-testid="split-prev-month"><ChevronLeft size={16}/></Button>
          <div className="px-3 font-mono text-sm tabular-nums" data-testid="split-period-label">{MONTH_NAMES[month - 1]} · {year}</div>
          <Button size="sm" variant="ghost" onClick={() => changeMonth(1)} data-testid="split-next-month"><ChevronRight size={16}/></Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-neutral-900 text-white rounded-2xl p-6" data-testid="kpi-commission">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400"><TrendingUp size={12}/> Comissão da matriz</div>
          <div className="mt-4 font-mono font-black text-4xl tracking-tight">{money(totalCommission)}</div>
          <div className="mt-1 text-[11px] text-neutral-500">retenção automática via MP</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="kpi-gross">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500"><Percent size={12}/> Volume bruto</div>
          <div className="mt-4 font-mono font-black text-4xl tracking-tight text-neutral-900">{money(totalGross)}</div>
          <div className="mt-1 text-[11px] text-neutral-500">{data?.grand_tx ?? 0} transações pagas</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="kpi-avg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500"><Percent size={12}/> Take-rate médio</div>
          <div className="mt-4 font-mono font-black text-4xl tracking-tight text-neutral-900">{avgTake.toFixed(2)}%</div>
          <div className="mt-1 text-[11px] text-neutral-500">comissão / bruto</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="font-heading font-bold">Por loja</div>
          <div className="text-[11px] text-neutral-500">{loading ? "Carregando…" : `${data?.by_store?.length ?? 0} loja(s) com receita no período`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[11px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Loja</th>
                <th className="text-right px-4 py-3 font-semibold">Transações</th>
                <th className="text-right px-4 py-3 font-semibold">Bruto</th>
                <th className="text-right px-4 py-3 font-semibold">Comissão</th>
                <th className="text-right px-6 py-3 font-semibold">Recebido pela loja</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {(data?.by_store || []).map((r) => (
                <tr key={r.store_id} className="hover:bg-neutral-50" data-testid={`split-row-${r.code || r.store_id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-neutral-900 text-white rounded-lg grid place-items-center">
                        {r.is_owner_store ? <Home size={14}/> : <StoreIcon size={14}/>}
                      </div>
                      <div>
                        <div className="font-semibold">{r.store_name}</div>
                        <div className="text-[11px] text-neutral-500 font-mono flex items-center gap-1.5">
                          {r.code || r.store_id}
                          {r.is_owner_store && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-full"><Home size={8}/> minha</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right px-4 py-4 font-mono">{r.tx_count}</td>
                  <td className="text-right px-4 py-4 font-mono">{money(r.gross)}</td>
                  <td className="text-right px-4 py-4 font-mono font-bold text-emerald-700">{r.commission > 0 ? money(r.commission) : <span className="text-neutral-300">—</span>}</td>
                  <td className="text-right px-6 py-4 font-mono">{money(r.net)}</td>
                </tr>
              ))}
              {!loading && (!data?.by_store || data.by_store.length === 0) && (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-neutral-400 text-sm" data-testid="split-empty">Nenhuma transação paga via Mercado Pago Marketplace neste período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-6 text-[11px] text-neutral-500">Somente transações pagas processadas via OAuth (loja conectada). Pagamentos processados pela conta padrão da plataforma não geram split e não aparecem aqui.</p>
    </div>
  );
}
